<script setup lang="ts">
import { onMounted, watch } from 'vue';

import { AppFirstLevelPanel } from '@libs/ui';

import { DynamicFullScreenMap, MapType } from '../maps';

import type { ActivityDetailsData, SelectTicketEventPayload } from './models';
import ActivityDetailsFooter from './components/ActivityDetailsFooter.vue';
import ActivityDetailsGallery from './components/ActivityDetailsGallery.vue';
import ActivityDetailsDescription from './components/description/ActivityDetailsDescription.vue';
import ActivityDetailsTickets from './components/ticket/ActivityDetailsTickets.vue';
import { useActivityDetailsStore } from './stores/activityDetailsStore';
import { useActivityDetailsMapStore } from './stores/activityDetailsMapStore';
import { useActivityDetailsTicketsStore } from './stores/activityDetailsTicketsStore';

interface ActivityDetailsPanelProps {
  activityDetails: ActivityDetailsData;
}

const props = defineProps<ActivityDetailsPanelProps>();

interface ActivityDetailsPanelEmits {
  (event: 'close'): void;
  (eventName: 'ticketSelected', eventPayload: SelectTicketEventPayload): void;
}
const emit = defineEmits<ActivityDetailsPanelEmits>();

const activityDetailsStore = useActivityDetailsStore();
const activityDetailsMapStore = useActivityDetailsMapStore();
const activityDetailsTicketsStore = useActivityDetailsTicketsStore();

// NOTE: Part of data required to create ActivityDetailsData interface is loaded in async manner,
// after activityDetails.vue is rendered to the screen. Hence we are updating inner state after component creation.
watch(
  () => props.activityDetails,
  () => {
    activityDetailsStore.setActivityDetails(props.activityDetails);
  },
  { immediate: true }
);

onMounted(() => {
  activityDetailsTicketsStore.setInitialSelectedDate();
});
</script>

<template>
  <AppFirstLevelPanel class="activity-details-panel" @close="emit('close')">
    <template #content>
      <div class="content-wrapper">
        <ActivityDetailsGallery
          v-if="
            activityDetailsStore.headerData &&
            props.activityDetails.staticInfo.images
          "
          :gallery-type="activityDetailsStore.galleryType"
          :product-text="activityDetailsStore.headerData.headerText"
          :images="props.activityDetails.staticInfo.images"
          :activity-code="props.activityDetails.productCode"
          @close="emit('close')"
        />
        <ActivityDetailsDescription />
        <ActivityDetailsTickets
          :product-code="props.activityDetails.productCode"
          :activity-type="props.activityDetails.staticInfo.typeOfHotel"
        />
      </div>
    </template>
    <template #footer>
      <ActivityDetailsFooter @close="emit('close')" />
    </template>
  </AppFirstLevelPanel>

  <DynamicFullScreenMap
    v-if="activityDetailsMapStore.shouldShowFullscreenMap"
    :map-configuration="{
      markers: activityDetailsMapStore.mapMarker,
      markerFormatters: activityDetailsMapStore.getMapMarkerFormatters(
        MapType.Dynamic
      ),
      startRectangle: activityDetailsMapStore.initialMapView,
    }"
    @close="activityDetailsMapStore.closeDynamicMapModal"
  />
</template>

<style scoped lang="scss">
.activity-details-panel {
  background-color: var(--color-white);

  .content-wrapper {
    @include for-large-devices-min {
      padding: var(--spacing-24) var(--spacing-24) var(--spacing-32)
        var(--spacing-24);
    }

    .divider {
      margin: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
      border-top: 1px solid var(--color-scrollbar);

      @include for-large-devices-min {
        margin: 0 0 var(--spacing-32) 0;
      }
    }
  }
}
</style>
